import isMobile from 'is-mobile';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import React, { useEffect, useState } from 'react';
import { userService } from '@/services/user.service';
import { useNavigate } from 'react-router-dom';

import SuccessMessage from '@/components/molecules/SuccessMessage';
import toast from 'react-hot-toast';
import { useWidth } from '@/hooks/useWidth';

import cameraIcon from '@/assets/icons/camera.svg';
import defaultUserImg from '@/assets/images/default-user.png';
import profileDiscardIcon from '@/assets/icons/profile-discard.svg';

import InputField from '../atoms/InputField';
import InfoModal from '../molecules/InfoModal';
import UserProfilePicInput from '../molecules/UserProfilePicInput';
import { Button } from '../ui/button';
import { useAuth } from '@/hooks/useAuth';

import { zodResolver } from '@hookform/resolvers/zod';
import { store } from '@/store';
import { getCurrentUser, setUser } from '@/store/slices/auth';
import { useSelector } from 'react-redux';

import { getCurrencySymbol } from '@/utils/budgetCalculations';

// Custom styles for PhoneInput to match InputField styling
const phoneInputStyles = `
  .phone-input-custom .PhoneInputInput {
    background-color: #F8F9FA !important;
    border-radius: 20px !important;
    border: 1px solid #DEE2E6 !important;
    font-weight: 500 !important;
    font-size: 1rem !important;
    padding: 0.585rem 1rem !important;
    outline: none !important;
    color: #495057 !important;
    width: 100% !important;
  }
  
  .phone-input-custom .PhoneInputCountry {
    margin-right: 0.65rem !important;
  }
  .phone-input-custom .PhoneInputCountrySelectArrow {
    margin-left: 0.65rem !important;
  }
  
  .phone-input-custom .PhoneInputInput::placeholder {
    color: #93999F !important;
  }
  
  .phone-input-custom .PhoneInputInput:focus {
    outline: none !important;
    border-color: #ADB5BD !important;
  }
  
  .phone-input-custom .PhoneInputCountrySelect {
    background-color: #F8F9FA !important;
    border: 1px solid #DEE2E6 !important;
    border-radius: 20px 0 0 20px !important;
    padding: 0.585rem 0.5rem !important;
    outline: none !important;
  }
  
  .phone-input-custom .PhoneInputCountrySelect:focus {
    outline: none !important;
    border-color: #ADB5BD !important;
  }
  
  .phone-input-custom .PhoneInputCountrySelectArrow {
    color: #495057 !important;
  }
  
  .phone-input-error .PhoneInputInput {
    border-color: #dc3545 !important;
  }
  
  .phone-input-error .PhoneInputCountrySelect {
    border-color: #dc3545 !important;
  }
  
  @media (min-width: 768px) {
    .phone-input-custom .PhoneInputInput {
      padding: 0.45rem 0.875rem !important;
      font-size: 1rem !important;
    }
    
    .phone-input-custom .PhoneInputCountrySelect {
      padding: 0.45rem 0.5rem !important;
    }
  }
`;

// Add styles to document head
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = phoneInputStyles;
  if (!document.head.querySelector('style[data-phone-input-custom]')) {
    styleElement.setAttribute('data-phone-input-custom', 'true');
    document.head.appendChild(styleElement);
  }
}

const createProfileSchema = z.object({
  firstName: z.string().min(2, 'First name is required'),
  lastName: z.string().min(2, 'Last name is required'),
  username: z.string().min(1, 'Username is required'),
  email: z.string().email(),
  phone: z
    .string()
    .optional()
    .refine((value) => !value || /^\+[1-9]\d{1,14}$/.test(value), {
      message: 'Invalid phone number, please input a valid phone number'
    })
});

const editProfileSchema = z.object({
  firstName: z.string().min(2, 'Must be greater than 2').optional(),
  lastName: z.string().min(2, 'Must be greater than 2').optional(),
  username: z.string().min(1, 'Username is required'),
  email: z.string().email(),
  phone: z
    .string()
    .optional()
    .refine((value) => !value || /^\+[1-9]\d{1,14}$/.test(value), {
      message: 'Invalid phone number, please input a valid phone number'
    }),
  hourlyRate: z.preprocess(
    (val) => {
      if (val === '' || val === null || val === undefined) return 0;
      const num = Number(val);
      return isNaN(num) ? 0 : num;
    },
    z.number().min(0, { message: 'Hourly rate must be 0 or greater' })
  )
});

type ProfileFormData = z.infer<typeof editProfileSchema>;

interface ProfileCreationFormProps {
  variant?: 'default' | 'edit';
  size?: string;
  profile?: any;
  onFormStateChange?: (state: { isDirty: boolean }) => void;
  onCancel?: () => void;
  onBeforeSuccessfulNavigate?: () => void;
}

const ProfileCreationForm = ({
  variant = 'default',
  size = '',
  profile = {},
  onFormStateChange,
  onCancel,
  onBeforeSuccessfulNavigate
}: ProfileCreationFormProps) => {
  const [showSuccess, setShowSuccess] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [selectedCountry, setSelectedCountry] = useState('IT');
  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);
  const [initialValues, setInitialValues] = useState<any>(null);

  const navigate = useNavigate();
  const { user } = useAuth();
  const currentUser = useSelector(getCurrentUser);
  const width = useWidth();
  const isMobileView = width < 768 || isMobile() ? true : false;
  const currencySymbol = getCurrencySymbol('EUR');

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    watch,
    formState: { errors, isSubmitting, isDirty: rhfIsDirty }
  } = useForm<ProfileFormData>({
    resolver: zodResolver(variant === 'edit' ? editProfileSchema : createProfileSchema),
    defaultValues: {
      email: '' + '',
      firstName: profile?.firstName || '',
      lastName: profile?.lastName || '',
      phone: profile?.phoneNumber || '',
      username: profile?.username || '',
      hourlyRate: profile?.hourlyRate ?? 0
    }
  });

  // update email when user data becomes available
  useEffect(() => {
    if (user?.emailAddresses?.[0]?.emailAddress) {
      setValue('email', user.emailAddresses[0].emailAddress);
    }
  }, [user, setValue]);

  // Store initial values after form is populated (for edit mode)
  useEffect(() => {
    if (variant === 'edit' && profile && !initialValues) {
      setInitialValues({
        firstName: profile?.firstName || '',
        lastName: profile?.lastName || '',
        username: profile?.username || '',
        email: user?.emailAddresses?.[0]?.emailAddress || profile?.email || '',
        phone: profile?.phoneNumber || '',
        hourlyRate: profile?.hourlyRate ?? 0
      });
    }
  }, [variant, profile, user, initialValues]);

  const hasFormChanges = () => {
    if (variant !== 'edit') return false;

    // If initial values are not ready yet, fall back to RHF dirtiness + image changes.
    if (!initialValues) return rhfIsDirty || selectedImage !== null;

    const currentValues = {
      firstName: watch('firstName') || '',
      lastName: watch('lastName') || '',
      username: watch('username') || '',
      email: watch('email') || '',
      phone: watch('phone') || '',
      hourlyRate: Number(watch('hourlyRate')) || 0
    };

    // Check if any field has changed
    const fieldsChanged = Object.keys(initialValues).some(
      (key) => currentValues[key as keyof typeof currentValues] !== initialValues[key as keyof typeof initialValues]
    );

    // Check if image was selected
    const imageChanged = selectedImage !== null;

    return fieldsChanged || imageChanged || rhfIsDirty;
  };

  useEffect(() => {
    if (!onFormStateChange) return;
    onFormStateChange({ isDirty: hasFormChanges() });
  }, [
    onFormStateChange,
    initialValues,
    selectedImage,
    watch('firstName'),
    watch('lastName'),
    watch('username'),
    watch('email'),
    watch('phone'),
    watch('hourlyRate')
  ]);

  const handleImageChange = (file: File | null) => {
    setSelectedImage(file);
  };

  const handleImageRemove = () => {
    setSelectedImage(null);
  };

  const handleOnBoardingClick = () => {
    if (variant === 'edit' && onCancel) {
      onCancel();
      return;
    }

    // Only show discard modal in edit mode if there are unsaved changes
    if (variant === 'edit' && hasFormChanges()) {
      setIsDiscardModalOpen(true);
      return;
    }

    // Otherwise navigate normally
    if (variant === 'edit') navigate('/profile');
    else navigate('/onboarding/overview');
  };

  const handleDiscardChanges = () => {
    setIsDiscardModalOpen(false);
    navigate('/profile');
  };

  const onSubmit = async (data: any) => {
    try {
      if (variant === 'edit') {
        const { data: updatedData } = await userService.updateProfile({
          firstName: data.firstName,
          lastName: data.lastName,
          phoneNumber: '',
          username: data.username,
          email: data.email,
          hourlyRate: Number(data.hourlyRate) || 0,
          profilePicture: selectedImage || undefined
        });

        store.dispatch(
          setUser({
            ...currentUser,
            firstName: updatedData.firstName,
            lastName: updatedData.lastName,
            name: updatedData.firstName + ' ' + updatedData.lastName,
            email: updatedData.email,
            phoneNumber: updatedData.phoneNumber,
            id: updatedData?.firebaseUid,
            photoURL: updatedData?.profilePicUrl ?? undefined,
            username: updatedData?.username,
            hourlyRate: updatedData?.hourlyRate ?? 0
          })
        );

        toast.success('Profile updated successfully!');
        onBeforeSuccessfulNavigate?.();
        navigate('/profile');
      } else {
        const { data: createdData } = await userService.createProfile({
          firstName: data.firstName,
          lastName: data.lastName,
          username: data.username,
          email: data.email,
          phoneNumber: '+1234567890',
          profilePicture: selectedImage || undefined
        });

        store.dispatch(
          setUser({
            firstName: createdData.firstName,
            lastName: createdData.lastName,
            name: createdData.firstName + ' ' + (createdData.lastName || ''),
            email: createdData.email,
            phoneNumber: createdData.phoneNumber,
            id: createdData.firebaseUid,
            photoURL: createdData.profilePicUrl,
            username: createdData.username,
            status: 'ACTIVE'
          })
        );

        toast.success('Profile created successfully!');
        setShowSuccess(true);
        handleOnBoardingClick();
      }
    } catch (error: any) {
      console.log(`Failed to ${variant === 'edit' ? 'update' : 'create'} profile:`, error?.message);
      if (error?.message.includes('firstName')) {
        setError('firstName', { message: error?.message });
      } else if (error?.message.includes('lastName')) {
        setError('lastName', { message: error?.message });
      } else if (error?.message.includes('Username')) {
        setError('username', { message: error?.message });
      } else if (error?.message.includes('phone')) {
        setError('phone', { message: error?.message });
      }
      // toast.error(`Failed to ${variant === 'edit' ? 'update' : 'create'} profile. Please try again.`);
    }
  };

  return (
    <div
      className={`w-full ${variant === 'default' ? 'pb-10' : 'pb-0'}  flex-1 h-full mx-auto laptop-h:h-fit ${showSuccess ? 'flex flex-col justify-between' : ''}`}
    >
      {(!showSuccess || isMobileView) && (
        <form
          onSubmit={handleSubmit(onSubmit)}
          className={`px-4 md:px-0 flex flex-col justify-center items-center md:items-start ${size === 'sm' ? 'md:mt-0 md:pt-0' : 'md:mt-4'} py-3`}
        >
          <UserProfilePicInput
            defaultUserImg={profile?.photoURL || defaultUserImg}
            cameraIcon={cameraIcon}
            onImageChange={handleImageChange}
            onImageRemove={handleImageRemove}
          />
          <div className="w-full mt-6 flex items-start gap-4">
            <InputField
              label={'Firstname'}
              placeholder="Firstname..."
              name="firstName"
              type="text"
              register={register}
              className="flex-1"
              error={errors.firstName}
              size={!isMobileView ? 'sm' : ''}
            />
            <InputField
              label={'Lastname'}
              placeholder="Lastname..."
              name="lastName"
              type="text"
              register={register}
              className="flex-1"
              error={errors.lastName}
              size={!isMobileView ? 'sm' : ''}
            />
          </div>
          <div className="w-full flex flex-col items-start gap-x-4 md:flex-row">
            <InputField
              className="w-full mt-5"
              label={'Username'}
              placeholder="Username..."
              name="username"
              type="text"
              register={register}
              error={errors.username}
              // disabled={variant === 'edit'}
              size={!isMobileView ? 'sm' : ''}
            />
            <InputField
              className="w-full mt-5"
              label={'Mail'}
              placeholder="Mail..."
              name="email"
              type="text"
              register={register}
              disabled
              size={!isMobileView ? 'sm' : ''}
            />
          </div>
          {variant === 'edit' && (
            <InputField
              className="w-full mt-5 md:w-1/2"
              label="Hourly Rate"
              placeholder="rate..."
              name="hourlyRate"
              type="number"
              register={register}
              error={errors.hourlyRate}
              size={!isMobileView ? 'sm' : ''}
              unitDescription={
                <p>
                  <span className="font-bold">{currencySymbol}</span> /Hours
                </p>
              }
              min={0}
            />
          )}
          {/* <div className="w-full flex flex-col items-start gap-x-4 md:flex-row">
            <div className="w-full mt-5 md:w-1/2 md:ml-0 md:mr-auto">
              <label
                className={`inline-block text-[#495057] font-medium text-xs ${!isMobileView ? 'md:mb-[0.45rem] md:text-sm' : 'md:text-lg mb-2'}`}
              >
                Phone Number
              </label>
              <PhoneInput
                placeholder="Enter phone number..."
                defaultCountry="IT"
                international={true}
                value={watch('phone') || ''}
                onChange={(value) => {
                  // Only update if the value is not just a country code or empty
                  const currentPhone = watch('phone') || '';
                  const currentCountryCode = currentPhone.split(' ')[0];

                  // If clearing to just country code, maintain the current country
                  if ((value && value.includes(' ')) || !value) {
                    setValue('phone', value || '');
                  } else if (value && !value.includes(' ') && currentPhone.includes(' ')) {
                    // If we're going from a full number to just country code, preserve it
                    setValue('phone', value);
                  } else {
                    setValue('phone', value || '');
                  }
                }}
                onCountryChange={(country) => {
                  const currentPhone = watch('phone') || '';
                  // Only change country if there's no current phone number or if explicitly changed by user
                  if (!currentPhone || !currentPhone.includes(' ')) {
                    setSelectedCountry(country || 'IT');
                  }
                }}
                country={selectedCountry}
                className={`phone-input-custom ${errors.phone ? 'phone-input-error' : ''}`}
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                  const input = e.target as HTMLInputElement;
                  const currentValue = input.value;
                  const cursorPosition = input.selectionStart || 0;

                  // Extract country code (everything before the first space)
                  const spaceIndex = currentValue.indexOf(' ');
                  const countryCodeLength = spaceIndex > 0 ? spaceIndex : currentValue.length;

                  // Prevent backspace/delete from removing any part of the country code
                  if (
                    (e.key === 'Backspace' || e.key === 'Delete') &&
                    cursorPosition <= countryCodeLength &&
                    currentValue.startsWith('+')
                  ) {
                    e.preventDefault();
                  }
                }}
              />
              {errors.phone && <p className="mt-2 text-xs text-red-600">{errors.phone.message}</p>}
            </div>
            <span></span>
          </div> */}

          <div
            className={`flex items-center flex-col md:flex-row-reverse w-full ${size === 'sm' ? 'justify-start gap-6 md:mt-[55.4px]' : 'justify-between md:mt-[86.4px]'}  `}
          >
            <Button
              type="submit"
              className={`mt-[86.4px] mb-5 w-full rounded-[1.5rem] text-base uppercase font-semibold h-[2.75rem] md:mb-0 md:mt-0  md:w-fit md:px-7 ${size === 'sm' ? 'md:h-[40px] md:text-[1rem] capitalize' : 'md:h-[4rem] md:text-[1.125rem]'}`}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'PROCESSING...' : variant === 'default' ? 'CREATE A PROFILE' : 'Save Changes'}
            </Button>
            <button
              disabled={isSubmitting}
              type="button"
              className={` font-semibold  md:mt-2 w-fit ${size === 'sm' ? 'text-lightRed' : 'text-primary'}`}
              onClick={handleOnBoardingClick}
            >
              {variant === 'default' ? 'Skip for now' : 'Cancel'}
            </button>
          </div>
        </form>
      )}

      {showSuccess && !isMobileView && (
        <>
          <SuccessMessage title="Account Created" description="You have successfully created an account in BatJet!" />

          <Button
            onClick={handleOnBoardingClick}
            className="mt-[86.4px] mb-5 w-fit rounded-[1.5rem] text-base font-semibold px-6 h-[4rem] text-[1.125rem] ml-auto mr-0"
          >
            GO TO ONBOARDING
          </Button>
        </>
      )}

      {showSuccess && isMobileView && (
        <InfoModal
          isOpen={showSuccess}
          variant="success"
          title="Account Created"
          message="You have successfully created an account in BatJet!"
          buttonText="Go to Onboarding"
          onButtonClick={handleOnBoardingClick}
        />
      )}

      {isDiscardModalOpen && (
        <InfoModal
          isOpen={isDiscardModalOpen}
          variant="confirm"
          title="Are you sure want to discard changes?"
          message="Unsaved change will not saved into your profile"
          buttonText="Discard"
          onButtonClick={handleDiscardChanges}
          icon={profileDiscardIcon}
          onClose={() => {
            setIsDiscardModalOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default ProfileCreationForm;
